/**
 * Fetch stock price period returns (1D / 1W / 1M / 1Y).
 *
 * Prefers the Vercel `/api/stock-returns` proxy (no CORS). Falls back to the
 * Supabase edge function when available. Sheet historical GOOGLEFINANCE cannot
 * be read via the Sheets API (Google returns #N/A).
 */

import { supabase } from './supabaseClient';

export interface PeriodReturns {
  daily: number | null;
  weekly: number | null;
  monthly: number | null;
  annual: number | null;
}

const EMPTY: PeriodReturns = {
  daily: null,
  weekly: null,
  monthly: null,
  annual: null,
};

const cache = new Map<string, { at: number; value: PeriodReturns }>();
const CACHE_TTL_MS = 15 * 60 * 1000;

const fetchViaVercelApi = async (
  symbols: string[]
): Promise<Record<string, PeriodReturns> | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;
    const res = await fetch('/api/stock-returns', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ symbols }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json?.returns || typeof json.returns !== 'object') return null;
    return json.returns as Record<string, PeriodReturns>;
  } catch {
    return null;
  }
};

const fetchViaEdgeFunction = async (
  symbols: string[]
): Promise<Record<string, PeriodReturns> | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('stock-period-returns', {
      body: { symbols },
    });
    if (error || !data?.returns || typeof data.returns !== 'object') return null;
    return data.returns as Record<string, PeriodReturns>;
  } catch {
    return null;
  }
};

/**
 * Batch-fetch period returns for unique symbols.
 * Results are cached ~15 minutes per symbol.
 */
export const fetchPeriodReturnsForSymbols = async (
  symbols: string[]
): Promise<Record<string, PeriodReturns>> => {
  const unique = Array.from(
    new Set(
      symbols
        .map(s => s.trim().toUpperCase())
        .filter(Boolean)
    )
  );
  if (unique.length === 0) return {};

  const now = Date.now();
  const out: Record<string, PeriodReturns> = {};
  const missing: string[] = [];

  for (const sym of unique) {
    const hit = cache.get(sym);
    if (hit && now - hit.at < CACHE_TTL_MS) {
      out[sym] = hit.value;
    } else {
      missing.push(sym);
    }
  }

  if (missing.length === 0) return out;

  const fetched =
    (await fetchViaVercelApi(missing)) ||
    (await fetchViaEdgeFunction(missing));

  if (fetched) {
    for (const sym of missing) {
      const value = fetched[sym] || { ...EMPTY };
      cache.set(sym, { at: now, value });
      out[sym] = value;
    }
    return out;
  }

  for (const sym of missing) {
    out[sym] = { ...EMPTY };
  }
  return out;
};
